/**
 * LOGOI LAB - useCustomWeights Hook
 * Manages user-customizable weight overrides for Λ, V, S metrics.
 * Persists to localStorage and provides reset-to-theory defaults.
 * 
 * @author Dr. José Manuel Cadena Ortiz de Montellano
 * @framework Cadena Strategic Systems
 */

import { useState, useCallback, useEffect } from 'react';
import { LOGOS_COMPONENTS, VIABILITY_WEIGHTS, ENTROPY_FACTORS } from '../core';

const STORAGE_KEY = 'logos-custom-weights';

export type WeightMetric = 'lambda' | 'viability' | 'entropy';

export interface CustomWeightsState {
  enabled: boolean;
  lambda: Record<string, number>;
  viability: Record<string, number>;
  entropy: Record<string, number>;
}

/** Extract default Λ weights from LOGOS_COMPONENTS */
function getDefaultLambdaWeights(): Record<string, number> {
  const w: Record<string, number> = {};
  for (const group of Object.values(LOGOS_COMPONENTS)) {
    for (const item of group.items) {
      w[item.key] = item.weight;
    }
  }
  return w;
}

/** Extract default S weights from ENTROPY_FACTORS */
function getDefaultEntropyWeights(): Record<string, number> {
  const w: Record<string, number> = {};
  for (const f of ENTROPY_FACTORS) {
    w[f.key] = f.weight;
  }
  return w;
}

export const DEFAULT_WEIGHTS: Omit<CustomWeightsState, 'enabled'> = {
  lambda: getDefaultLambdaWeights(),
  viability: { ...VIABILITY_WEIGHTS },
  entropy: getDefaultEntropyWeights(),
};

function loadFromStorage(): CustomWeightsState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

function saveToStorage(state: CustomWeightsState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch { /* quota exceeded etc */ }
}

/**
 * Hook for managing custom weight overrides.
 * Returns current weights, setters, and reset functions.
 */
export function useCustomWeights() {
  const [weights, setWeights] = useState<CustomWeightsState>(() => {
    const stored = loadFromStorage();
    return stored ?? {
      enabled: false,
      lambda: { ...DEFAULT_WEIGHTS.lambda },
      viability: { ...DEFAULT_WEIGHTS.viability },
      entropy: { ...DEFAULT_WEIGHTS.entropy },
    };
  });

  // Persist on change
  useEffect(() => {
    saveToStorage(weights);
  }, [weights]);

  const setEnabled = useCallback((enabled: boolean) => {
    setWeights(prev => ({ ...prev, enabled }));
  }, []);

  const updateWeight = useCallback((metric: WeightMetric, key: string, value: number) => {
    setWeights(prev => ({
      ...prev,
      [metric]: { ...prev[metric], [key]: value },
    }));
  }, []);

  const resetMetric = useCallback((metric: WeightMetric) => {
    setWeights(prev => ({
      ...prev,
      [metric]: { ...DEFAULT_WEIGHTS[metric] },
    }));
  }, []);

  const resetAll = useCallback(() => {
    setWeights({
      enabled: false,
      lambda: { ...DEFAULT_WEIGHTS.lambda },
      viability: { ...DEFAULT_WEIGHTS.viability },
      entropy: { ...DEFAULT_WEIGHTS.entropy },
    });
  }, []);

  // Overrides to pass to compute functions (null if not enabled)
  const lambdaOverrides = weights.enabled ? weights.lambda : undefined;
  const viabilityOverrides = weights.enabled ? weights.viability : undefined;
  const entropyOverrides = weights.enabled ? weights.entropy : undefined;

  return {
    weights,
    enabled: weights.enabled,
    setEnabled,
    updateWeight,
    resetMetric,
    resetAll,
    lambdaOverrides,
    viabilityOverrides,
    entropyOverrides,
    DEFAULT_WEIGHTS,
  };
}
